import React, { useState, useContext, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import AutoResizedTextField from "../../components/mdc/AutoResizedTextField.js";
import { GlobalContext } from "../../context/GlobalContext";

const RESTORABLE_ERROR_CODES = new Set(["content_moderated", "moderation_unavailable"]);

const MessageInput = forwardRef((props, ref) => {
    const { auth, viewerMode } = useContext(GlobalContext);
    const { userId } = auth;
    const [message, setMessage] = useState(localStorage.getItem("myInputValue") || "");
    const [isSending, setIsSending] = useState(false);
    // Expose utility functions to the parent via ref
    useImperativeHandle(ref, () => ({
        reset() {
            setMessage("");
            localStorage.removeItem("myInputValue");
        },
        setValue(val) {
            setMessage(val);
            if (val) {
                localStorage.setItem("myInputValue", val);
            } else {
                localStorage.removeItem("myInputValue");
            }
        },
    }));

    if (viewerMode) return null;

    const handleInputChange = (event) => {
        const inputValue = event.target.value;
        setMessage(inputValue);
        localStorage.setItem("myInputValue", inputValue);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedMessage = message.trim();
        if (!trimmedMessage || isSending) return;

        setIsSending(true);
        setMessage("");
        localStorage.removeItem("myInputValue");

        // Immediately update conversation history with temporary messages.
        props.setConversationHistory((prevHistory) => {
            // Remove any existing temporary messages.
            const newHistory = prevHistory.filter(
                (msg) => msg.id !== "temp-user" && msg.id !== "temp-assistant"
            );

            let updatedHistory = [];

            // If the user's target language is not set,
            // the temporary user message shows as done (i.e. pre-translated).
            if (!props.preferences.language) {
                updatedHistory = [
                    ...newHistory,
                    {
                        id: "temp-user",
                        senderId: userId,
                        senderType: "user",
                        targetLanguageMessage: { status: "done", value: trimmedMessage },
                    },
                ];
            } else {
                // Otherwise, show the user message as loaded (original message) with target set as connecting.
                updatedHistory = [
                    ...newHistory,
                    {
                        id: "temp-user",
                        senderId: userId,
                        senderType: "user",
                        originalMessage: { status: "loaded", value: trimmedMessage },
                        targetLanguageMessage: { status: "connecting" },
                    },
                ];
            }
            return updatedHistory;
        });

        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/message/send-message`,
                {
                    message: trimmedMessage,
                    conversationId: props.conversationDetails.conversationId,
                },
                { withCredentials: true }
            );
            localStorage.removeItem("myInputValue");
        } catch (error) {
            console.error("Error sending message:", error);
            const errorCode = error.response?.data?.code;
            const errorMessage =
                error.response?.data?.message ||
                "Your message could not be sent. Please try again.";
            if (RESTORABLE_ERROR_CODES.has(errorCode)) {
                setMessage(trimmedMessage);
                localStorage.setItem("myInputValue", trimmedMessage);
            }
            props.setConversationHistory((prevHistory) =>
                prevHistory.map((msg) =>
                    msg.id === "temp-user"
                        ? {
                              ...msg,
                              targetLanguageMessage: {
                                  status: "error",
                                  statusMessage: errorMessage,
                              },
                          }
                        : msg
                )
            );
        } finally {
            setIsSending(false);
        }
    };

    return (
        <form className="message-form" onSubmit={handleSubmit}>
            <AutoResizedTextField
                variant="outlined"
                id="userInput"
                type="textarea"
                value={message}
                rows="1"
                placeholder="Type your mixed language message..."
                onInput={handleInputChange}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                        handleSubmit(e);
                        e.preventDefault();
                    }
                }}
            />
            <md-icon-button type="submit" className="send-button" softDisabled={isSending}>
                <md-icon filled>send</md-icon>
            </md-icon-button>
        </form>
    );
});

export default MessageInput;
